import moment from 'moment';
import { Settlement } from '../../../models/Entities/Settlements/Settlement';
import { Contract } from '../../../models/Entities/Contracts/Contract';
import { Protocol } from '../../../models/Entities/Protocols/Protocol';
import { ProviderImportJob } from '../../../models/Entities/ProviderConsumptionImport/ProviderConsumptionImport';
import { SettlementStatusEnum } from '../../../models/Enums/SettlementStatusEnum';
import { StatusTone } from '../../../theme/guideTokens';
import { MenuItems } from '../../SideMenu/SideMenuItems';
import { MenuItem } from '../../SideMenu/SideMenuModels';
import { ExpiringContractsWindow } from '../Dashboard.Types';

//#region Liquidaciones

/** Pendientes de aprobación: terminadas (3), que es el único estado con "Confirmar" / "Descartar" en el encabezado. */
export const PENDING_APPROVAL_STATUS = SettlementStatusEnum.FINISHED;

export const pendingApprovalSettlements = (list: Settlement[]): Settlement[] =>
  list.filter((settlement) => settlement.status === PENDING_APPROVAL_STATUS).sort((a, b) => toTime(b.created) - toTime(a.created));

export type SettlementGroup = 'calculating' | 'pendingApproval' | 'toCollect' | 'paid' | 'failed';

export const SETTLEMENT_GROUPS: SettlementGroup[] = ['calculating', 'pendingApproval', 'toCollect', 'paid', 'failed'];

export const settlementGroupOf = (status: number | undefined): SettlementGroup | undefined => {
  switch (status) {
    case SettlementStatusEnum.REGISTERED:
    case SettlementStatusEnum.IN_PROGRESS:
      return 'calculating';
    case SettlementStatusEnum.FINISHED:
      return 'pendingApproval';
    case SettlementStatusEnum.CONFIRMED:
    case SettlementStatusEnum.DEBT_UNPAID:
      return 'toCollect';
    case SettlementStatusEnum.PAID:
      return 'paid';
    case SettlementStatusEnum.FAIL:
      return 'failed';
    default:
      return undefined;
  }
};

export const settlementGroupTone: Record<SettlementGroup, Exclude<StatusTone, 'brand'>> = {
  calculating: 'info',
  pendingApproval: 'warning',
  toCollect: 'confirmed',
  paid: 'success',
  failed: 'danger',
};

export interface SettlementGroupSummary {
  group: SettlementGroup;
  count: number;
  /** Importe total por moneda (sólo las liquidaciones con totales). */
  amounts: { currencyCode: string; amount: number }[];
}

export const summarizeSettlements = (list: Settlement[]): SettlementGroupSummary[] =>
  SETTLEMENT_GROUPS.map((group) => {
    const members = list.filter((settlement) => settlementGroupOf(settlement.status) === group);
    const byCurrency = new Map<string, number>();
    members.forEach((settlement) => {
      const { amount, currencyCode } = settlementAmount(settlement);
      if (amount === undefined) return;
      byCurrency.set(currencyCode, (byCurrency.get(currencyCode) ?? 0) + amount);
    });
    return {
      group,
      count: members.length,
      amounts: Array.from(byCurrency.entries()).map(([currencyCode, amount]) => ({ currencyCode, amount })),
    };
  });

/** Importe de la liquidación: suma de sus totales en la moneda de la liquidación. */
export const settlementAmount = (settlement: Settlement): { amount: number | undefined; currencyCode: string } => {
  const totals = settlement.totals ?? [];
  if (totals.length === 0) return { amount: undefined, currencyCode: settlement.currencyCode };
  const currencyCode = totals[0].currencyCode || settlement.currencyCode;
  const amount = totals.filter((total) => (total.currencyCode || settlement.currencyCode) === currencyCode).reduce((sum, total) => sum + (total.totalValue ?? 0), 0);
  return { amount, currencyCode };
};

//#endregion

//#region Contratos

export const CONTRACT_ACTIVE_STATUS = 1;

export interface ExpiringContract {
  contract: Contract;
  /** Días hasta el fin de vigencia (negativo si ya venció). */
  daysLeft: number;
}

/** Contratos activos cuya vigencia termina dentro de la ventana (incluye los ya vencidos que siguen activos). */
export const expiringContracts = (list: Contract[], days: ExpiringContractsWindow, now: Date = new Date()): ExpiringContract[] => {
  const today = moment(now).startOf('day');
  return list
    .filter((contract) => contract.contractStatus === CONTRACT_ACTIVE_STATUS && !!contract.contractEndDate)
    .map((contract) => ({ contract, daysLeft: moment(contract.contractEndDate).startOf('day').diff(today, 'days') }))
    .filter((item) => item.daysLeft <= days)
    .sort((a, b) => a.daysLeft - b.daysLeft);
};

export const expiringTone = (daysLeft: number): StatusTone => (daysLeft < 0 ? 'danger' : daysLeft <= 15 ? 'warning' : 'info');

export const pinnedContracts = (list: Contract[], ids: number[]): Contract[] =>
  ids.map((id) => list.find((contract) => contract.id === id)).filter((contract): contract is Contract => !!contract);

export const contractStatusTone = (status: number | undefined): StatusTone => {
  switch (status) {
    case 1:
      return 'success';
    case 2:
    case 3:
      return 'warning';
    case 4:
    case 5:
      return 'danger';
    case 6:
      return 'neutral';
    default:
      return 'neutral';
  }
};

export const contractStatusKey = (status: number | undefined): string => {
  switch (status) {
    case 1:
      return 'active';
    case 2:
      return 'inactive';
    case 3:
      return 'suspended';
    case 4:
      return 'expired';
    case 5:
      return 'cancelled';
    case 6:
      return 'terminated';
    default:
      return 'unknown';
  }
};

//#endregion

//#region Protocolos

export interface ProtocolStatusCount {
  title: string;
  count: number;
}

export interface ProtocolSummary {
  total: number;
  active: number;
  closed: number;
  /** Los estados más frecuentes entre los activos, de mayor a menor. */
  byStatus: ProtocolStatusCount[];
}

export const summarizeProtocols = (list: Protocol[], maxStatuses = 5): ProtocolSummary => {
  const active = list.filter((protocol) => protocol.active && !protocol.closed);
  const counts = new Map<string, number>();
  active.forEach((protocol) => {
    const title = protocol.status?.title?.trim() || '—';
    counts.set(title, (counts.get(title) ?? 0) + 1);
  });
  return {
    total: list.length,
    active: active.length,
    closed: list.length - active.length,
    byStatus: Array.from(counts.entries())
      .map(([title, count]) => ({ title, count }))
      .sort((a, b) => b.count - a.count || a.title.localeCompare(b.title))
      .slice(0, maxStatuses),
  };
};

//#endregion

//#region Importaciones

export const recentImportJobs = (jobs: Record<string, ProviderImportJob> | undefined, max = 8): ProviderImportJob[] =>
  Object.values(jobs ?? {})
    .sort((a, b) => toTime(b.requestedAt ?? b.startedAt) - toTime(a.requestedAt ?? a.startedAt))
    .slice(0, max);

//#endregion

//#region Accesos directos

/** Secciones de la aplicación que se pueden fijar como acceso directo (las del menú lateral, sin "Inicio"). */
export const quickLinkItems = (): MenuItem[] => MenuItems.filter((item) => !!item.target && !!item.name);

export const DEFAULT_QUICK_LINK_TARGETS = ['contracts', 'settlements', 'protocolsagreements', 'Providers', 'sponsors', 'files'];

export const selectedQuickLinks = (targets: string[] | undefined): MenuItem[] => {
  const wanted = targets && targets.length > 0 ? targets : DEFAULT_QUICK_LINK_TARGETS;
  const items = quickLinkItems();
  return wanted.map((target) => items.find((item) => item.target === target)).filter((item): item is MenuItem => !!item);
};

//#endregion

const toTime = (value: Date | string | null | undefined): number => {
  if (!value) return 0;
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : 0;
};
